import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { API_SERVICE } from '@/constants/api.constant';
import { DELETE, GET, POST, PUT } from '@/lib/fetch';
import { forceDownload } from '@/services/fn';
import type {
	ApproveDocumentSchema,
	BankDataSchema,
	DocumentDataSchema,
	MainSchema,
	PicDataSchema,
	SegmentationDataSchema
} from '@/types/form/account-manager/form';
import type {
	DataTableResponse,
	ErrorApiResponse,
	SuccessResponse
} from '@/types/response';
import type {
	VendorBankDataResponse,
	VendorDetail,
	VendorDocumentDataResponse,
	VendorMainDataResponse,
	VendorPicDataResponse,
	VendorSectionStatus,
	VendorSegmentationDataResponse
} from '@/types/vendor/vendor';

import { userQueries } from '../user/user.queries';

export const vendorQueries = {
	keys: () => 'vendors',
	mainData: () =>
		queryOptions<SuccessResponse<VendorMainDataResponse>, ErrorApiResponse>({
			queryKey: [vendorQueries.keys(), 'mainData'],
			queryFn: () => GET({ path: `/main-data`, service: API_SERVICE.API })
		}),
	bankData: () =>
		queryOptions<DataTableResponse<VendorBankDataResponse>, ErrorApiResponse>({
			queryKey: [vendorQueries.keys(), 'bankData'],
			queryFn: () => GET({ path: `/bank-data`, service: API_SERVICE.API })
		}),
	segmentationData: () =>
		queryOptions<
			DataTableResponse<VendorSegmentationDataResponse>,
			ErrorApiResponse
		>({
			queryKey: [vendorQueries.keys(), 'segmentationData'],
			queryFn: () => GET({ path: `/segmentation-data`, service: API_SERVICE.API })
		}),
	picData: () =>
		queryOptions<DataTableResponse<VendorPicDataResponse>, ErrorApiResponse>({
			queryKey: [vendorQueries.keys(), 'picData'],
			queryFn: () => GET({ path: `/person-responsible`, service: API_SERVICE.API })
		}),

	documentData: () =>
		queryOptions<SuccessResponse<VendorDocumentDataResponse[]>, ErrorApiResponse>(
			{
				queryKey: [vendorQueries.keys(), 'documentData'],
				queryFn: () => GET({ path: `/document-data`, service: API_SERVICE.API })
			}
		),

	sectionStatus: () =>
		queryOptions<SuccessResponse<VendorSectionStatus>, ErrorApiResponse>({
			queryKey: [vendorQueries.keys(), 'sectionStatus'],
			queryFn: () =>
				GET({ path: `/vendor/section-status`, service: API_SERVICE.API })
		}),

	vendor: (id: string) =>
		queryOptions<SuccessResponse<VendorDetail>>({
			queryKey: [vendorQueries.keys(), 'detail', id],
			queryFn: () => GET({ path: `/vendor/${id}`, service: API_SERVICE.API })
		}),
	vendorDocument: (id: string) =>
		queryOptions<SuccessResponse<VendorDocumentDataResponse[]>>({
			queryKey: [vendorQueries.keys(), 'detail', 'document', id],
			queryFn: () =>
				GET({ path: `/vendor/${id}/document-data`, service: API_SERVICE.API })
		})
};

export const vendorMutations = {
	mainData: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		MainSchema
	>({
		mutationFn: (body) =>
			POST({ path: `/main-data`, service: API_SERVICE.API }, { body }),
		meta: {
			invalidates: [
				vendorQueries.mainData().queryKey,
				vendorQueries.sectionStatus().queryKey
			]
		},
		mutationKey: ['mainData']
	}),
	bankData: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		BankDataSchema
	>({
		mutationFn: (body) =>
			POST(
				{ path: '/bank-data', service: API_SERVICE.API },
				{ body: { ...body, vendorId: null } }
			),
		meta: {
			invalidates: [
				vendorQueries.bankData().queryKey,
				vendorQueries.sectionStatus().queryKey
			]
		}
	}),
	bankDataUpdate: (id?: number) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse, BankDataSchema>({
			mutationFn: (body) =>
				PUT(
					{ path: `/bank-data/${id}`, service: API_SERVICE.API },
					{ body: { ...body, vendorId: null } }
				),
			meta: {
				invalidates: [vendorQueries.bankData().queryKey]
			}
		}),
	bankDataDelete: (id: number) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse>({
			mutationFn: () =>
				DELETE({ path: `/bank-data/${id}`, service: API_SERVICE.API }),
			meta: {
				invalidates: [
					vendorQueries.bankData().queryKey,
					vendorQueries.sectionStatus().queryKey
				]
			}
		}),
	segmentationData: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		SegmentationDataSchema
	>({
		mutationFn: (body) =>
			POST(
				{ path: '/segmentation-data', service: API_SERVICE.API },
				{ body: { ...body, vendorId: null } }
			),
		meta: {
			invalidates: [
				vendorQueries.segmentationData().queryKey,
				vendorQueries.sectionStatus().queryKey
			]
		}
	}),
	segmentationDataUpdate: (id?: number) =>
		mutationOptions<
			SuccessResponse<string>,
			ErrorApiResponse,
			SegmentationDataSchema
		>({
			mutationFn: (body) =>
				PUT(
					{ path: `/segmentation-data/${id}`, service: API_SERVICE.API },
					{ body: { ...body, vendorId: null } }
				),
			meta: {
				invalidates: [vendorQueries.segmentationData().queryKey]
			}
		}),
	segmentationDataDelete: (id: number) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse>({
			mutationFn: () =>
				DELETE({ path: `/segmentation-data/${id}`, service: API_SERVICE.API }),
			meta: {
				invalidates: [
					vendorQueries.segmentationData().queryKey,
					vendorQueries.sectionStatus().queryKey
				]
			}
		}),
	picData: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		PicDataSchema
	>({
		mutationFn: (body) =>
			POST(
				{ path: '/person-responsible', service: API_SERVICE.API },
				{ body: { ...body, vendorId: null } }
			),
		meta: {
			invalidates: [
				vendorQueries.picData().queryKey,
				vendorQueries.sectionStatus().queryKey
			]
		}
	}),
	picDataUpdate: (id?: number) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse, PicDataSchema>({
			mutationFn: (body) =>
				PUT(
					{ path: `/person-responsible/${id}`, service: API_SERVICE.API },
					{ body: { ...body, vendorId: null } }
				),
			meta: {
				invalidates: [vendorQueries.picData().queryKey]
			}
		}),
	picDataDelete: (id: number) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse>({
			mutationFn: () =>
				DELETE({ path: `/person-responsible/${id}`, service: API_SERVICE.API }),
			meta: {
				invalidates: [
					vendorQueries.picData().queryKey,
					vendorQueries.sectionStatus().queryKey
				]
			}
		}),

	documentDataUpdate: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		DocumentDataSchema
	>({
		mutationFn: (body) =>
			POST(
				{ path: `/document-data`, service: API_SERVICE.API },
				{ body: { ...body } }
			),
		meta: {
			invalidates: [
				vendorQueries.documentData().queryKey,
				vendorQueries.sectionStatus().queryKey
			]
		}
	}),

	photo: mutationOptions<
		SuccessResponse<string>,
		ErrorApiResponse,
		{ file_name: string | null, path: 'profile-image' | 'cover-image' }
	>({
		mutationFn: ({ path, ...body }) =>
			PUT(
				{
					path: `/main-data/${path}`,
					service: API_SERVICE.API
				},
				{ body }
			),
		meta: {
			invalidates: [vendorQueries.mainData().queryKey, userQueries.user().queryKey]
		}
	}),

	submitRequest: mutationOptions<SuccessResponse<string>, ErrorApiResponse>({
		mutationFn: () =>
			POST({
				path: '/vendor/send-request',
				service: API_SERVICE.API
			}),
		meta: {
			invalidates: [
				vendorQueries.sectionStatus().queryKey,
				vendorQueries.documentData().queryKey
			]
		}
	}),

	approveDocument: (id: number, vendorId: string) =>
		mutationOptions<
			SuccessResponse<string>,
			ErrorApiResponse,
			ApproveDocumentSchema
		>({
			mutationFn: (body) =>
				POST(
					{
						path: `/document-data/${id}/approval`,
						service: API_SERVICE.API
					},
					{ body }
				),
			meta: {
				invalidates: [
					vendorQueries.vendorDocument(vendorId).queryKey,
					vendorQueries.vendor(vendorId).queryKey
				]
			}
		}),

	approveRequest: (id: string) =>
		mutationOptions<SuccessResponse<string>, ErrorApiResponse>({
			mutationFn: () =>
				POST({
					path: `/vendor/${id}/review-document`,
					service: API_SERVICE.API
				}),
			meta: {
				invalidates: [
					vendorQueries.vendorDocument(id).queryKey,
					vendorQueries.vendor(id).queryKey
				]
			}
		}),

	templateStatementOfInterest: mutationOptions<
		SuccessResponse<{ url: string[] }>,
		ErrorApiResponse
	>({
		mutationFn: () =>
			GET({
				path: '/files/statement-freedom-of-interest',
				service: API_SERVICE.API
			}),
		onSuccess: ({ data: { url } }) => {
			const [doc, xls] = url as [string, string];
			forceDownload(doc, 'Surat Pernyataan Bebas Konflik Kepentingan.doc');
			forceDownload(xls, 'Surat Pernyataan Bebas Konflik Kepentingan.xls');
		}
	}),
	templateStatementAuthenticity: mutationOptions<
		SuccessResponse<{ url: string }>,
		ErrorApiResponse
	>({
		mutationFn: () =>
			GET({
				path: '/files/statement-authenticity-document',
				service: API_SERVICE.API
			}),
		onSuccess: ({ data: { url } }) => {
			forceDownload(url, 'Surat Pernyataan Kebenaran Dokumen.pdf');
		}
	})
};
